/**
 * Batch Transformation Utilities
 * 
 * High-level functions for transforming multiple objects at once
 */

import { AppointmentDto, ContinuousAppointmentDto, WorkingHoursDto } from "../../../../api-client";
import { 
  CalendarEvent, 
  AppointmentTransformOptions, 
  TimeslotTransformOptions,
  TimeslotBatchData,
  ClientTimeSlot,
  ClientLunchBreak
} from "./types";
import { adaptAppointmentFromApi, adaptContinuousAppointmentFromApi } from "./api-to-client";
import { adaptAppointmentToCalendarEvent, adaptTimeSlotToCalendarEvent, adaptContinuousAppointmentToCalendarEvent } from "./client-to-display";
import { 
  adaptEventTimeSlotFromApi,
  adaptContinuousEventTimeSlotFromApi,
  adaptLeaveOfAbsenceFromApi,
  adaptTimeSlotFromApi,
  adaptDerivedLunchBreak,
  clientLunchBreakToCalendarEvent
} from "./api-to-client";
import { adaptContinuousTimeSlotToCalendarEvent } from "./client-to-display/timeslot.adapter";

/**
 * Transform multiple appointments to calendar events
 */

//  const mergeGroupPatientIntoTherapistAppointments = (appointments: any[]): any[] => {
//    const therapistMap = new Map<string, any>();
//    const participants: any[] = [];
//    const result: any[] = [];

//    // 1 Collect therapist & patient appointments
//    for (const appointment of appointments) {
//      const therapistSlot = appointment.timeSlots?.find(
//        (ts: any) => ts.type === "GroupTimeSlotTherapist",
//      );

//      const patientSlot = appointment.timeSlots?.find(
//        (ts: any) => ts.type === "GroupTimeSlotPatient" && ts.groupAppointmentId,
//      );

//      if (therapistSlot) {
//        therapistMap.set(appointment.id, {
//          ...appointment,
//          participants: [],
//        });
//        continue;
//      }

//      if (patientSlot) {
//        participants.push(appointment);
//        continue;
//      }

//      result.push(appointment);
//    }

//    // 2 Attach patients ONLY when groupAppointmentId matches therapist.id
//    for (const patientApp of participants) {
//      const patientSlot = patientApp.timeSlots.find((ts: any) => ts.type === "GroupTimeSlotPatient");

//      const therapistApp = therapistMap.get(patientSlot.groupAppointmentId);

//      if (therapistApp) {
//        therapistApp.participants.push(patientApp);
//      } else {
//        // fallback if therapist not found
//        result.push(patientApp);
//      }
//    }

//    return [...therapistMap.values(), ...result];
//  };
 export const adaptAppointmentsToCalendarEvents = (
   appointments: AppointmentDto[],
   options: AppointmentTransformOptions = {},
 ): CalendarEvent[] => {
   const clientAppointments = appointments
     .map((apt) => adaptAppointmentFromApi(apt))
     .flatMap((clientApt) => adaptAppointmentToCalendarEvent(clientApt, options));
   return clientAppointments;
 };

export const adaptContinuousAppointmentsToCalendarEvents = (
  continuousAppointments: ContinuousAppointmentDto[],
  options: AppointmentTransformOptions = {}
): CalendarEvent[] => {
  return continuousAppointments
    .map(apt => adaptContinuousAppointmentFromApi(apt))
    .flatMap(clientApt => adaptContinuousAppointmentToCalendarEvent(clientApt, options));
};

/**
 * Helper function to check if a timeslot overlaps with any Leave of Absence for the same user
 */
const overlapsWithLoA = (
  timeSlot: ClientTimeSlot,
  leaveOfAbsences: ClientTimeSlot[]
): boolean => {
  if (!timeSlot.start || !timeSlot.end || !timeSlot.user?.id) {
    return false;
  }

  return leaveOfAbsences.some(loa => {
    if (!loa.start || !loa.end || loa.user?.id !== timeSlot.user?.id) {
      return false;
    }
    // Check for overlap: timeSlot starts before LoA ends AND timeSlot ends after LoA starts
    return timeSlot.start < loa.end && timeSlot.end > loa.start;
  });
};

/**
 * Transform mixed timeslot types to calendar events
 * do not use for continouous timeslots
 */
export const adaptTimeSlotsToCalendarEvents = (
  data: TimeslotBatchData,
  options: TimeslotTransformOptions = {}
): CalendarEvent[] => {
  const events: CalendarEvent[] = [];
  
  // Convert LoAs to ClientTimeSlot format for overlap checking
  const leaveOfAbsencesClient = (data.leaveOfAbsences || []).map(adaptLeaveOfAbsenceFromApi);
  
  // Handle regular timeslots (GoneFishing)
  if (data.goneFishings) {
    events.push(...data.goneFishings
      .map((ts) => adaptTimeSlotFromApi(ts, false))
      .map(ts => ({ ...ts, type: 'GoneFishing' as const }))
      .map(ts => adaptTimeSlotToCalendarEvent(ts, options))
    );
  }
  
  // Handle leave of absences
  if (data.leaveOfAbsences) {
    events.push(...leaveOfAbsencesClient
      .map(ts => adaptTimeSlotToCalendarEvent(ts, options))
    );
  }
  
  // Handle event timeslots - filter out those overlapping with LoAs
  if (data.eventTimeSlots) {
    events.push(...data.eventTimeSlots
      .map(adaptEventTimeSlotFromApi)
      .filter(ts => !overlapsWithLoA(ts, leaveOfAbsencesClient))
      .map(ts => adaptTimeSlotToCalendarEvent(ts, options))
    );
  }
  
  // Handle lunch breaks - filter out those overlapping with LoAs
  if (data.lunchBreaks) {
    events.push(...(data.lunchBreaks as WorkingHoursDto[])
      .map(adaptDerivedLunchBreak)
      .filter(lb => {
        // Check overlap with LoAs for this user
        if (!lb.start || !lb.end || !lb.userId) return true;
        return !leaveOfAbsencesClient.some(loa => {
          if (!loa.start || !loa.end || loa.user?.id !== lb.userId) return false;
          return lb.start < loa.end && lb.end > loa.start;
        });
      })
      .map(lb => clientLunchBreakToCalendarEvent(lb))
    );
  }
  
  // Handle travel times
  if (data.travelTimes) {
    events.push(...data.travelTimes
      .map((ts) => adaptTimeSlotFromApi(ts, false))
      .map(ts => ({ ...ts, type: 'TravelTime' as const }))
      .map(ts => adaptTimeSlotToCalendarEvent(ts, options))
    );
  }
  
  
  // Patient Slots go into column with ID "0"
  if (data.patientSlots) {
    events.push(...data.patientSlots
      .map((ts) => adaptTimeSlotFromApi(ts, false))
      .map(ts => ({ ...ts, type: 'PatientSlot' as const }))
      .map(ts => adaptTimeSlotToCalendarEvent(ts, options, true))
    );
  }
  return events;
};

export const adaptContinuousTimeSlotsToCalendarEvents = (
  data: TimeslotBatchData,
  options: TimeslotTransformOptions = {}
): CalendarEvent[] => {
  const events: CalendarEvent[] = [];
  
  // Handle continuous event timeslots
  if (data.continuousEventTimeSlots) {
    events.push(...data.continuousEventTimeSlots
      .map(adaptContinuousEventTimeSlotFromApi)
      .map(ts => adaptContinuousTimeSlotToCalendarEvent(ts, options))
    );
  }
  
  return events;
};


